import { FC } from "react";

import { Collapse, Stack } from "@mui/material";
import { ProductDonationRecurrenceInterval, SuggestedAmountsCount } from "@simplyk/common";
import { useForm } from "react-hook-form";
import { v4 } from "uuid";

import { DefaultOptionsBlock } from "./DefaultOptionsBlock/DefaultOptionsBlock";
import { GivingFrequenciesAndAmount } from "./GivingFrequenciesAndAmount/GivingFrequenciesAndAmount";
import { PromoteMonthlyGivingBlock } from "./PromoteMonthlyGivingBlock";
import { SmartAmountsBlock } from "./SmartAmountsBlock/SmartAmountsBlock";
import { SpecifyImpactForAmountSelected } from "./SpecifyImpactForAmountSelected/SpecifyImpactForAmountSelected";
import { StepSuggestedDonationAmountsFormData } from "./types";

import { trpc } from "@/common/helpers/trpc";
import { useTranslate } from "@/common/hooks/useTranslate";
import { LiveEditorBlock } from "@/components/LiveEditorElements/LiveEditorBlock/LiveEditorBlock";
import { useLiveFormEditorContext } from "@/features/LiveFormEditor/common/LiveFormEditorContext";
import { useUpdateDonationDataInPreview } from "@/features/LiveFormEditor/common/useUpdateLiveTicketingPreview/useUpdateDonationDataInPreview";
import { FormStepContainer } from "@/features/LiveFormEditor/Steps/FormStepContainer";
import {
  DONATION_FREQUENCIES,
  getEnabledFrequencies,
  resizeAmounts,
} from "@/features/LiveFormEditor/Steps/StepSuggestedDonationAmounts/helpers/donationFrequencies";
import { getAmountsToSave } from "@/features/LiveFormEditor/Steps/StepSuggestedDonationAmounts/helpers/getAmountsToSave";
import {
  generateDefaultAmounts,
  useGetDefaultValue,
} from "@/features/LiveFormEditor/Steps/StepSuggestedDonationAmounts/helpers/useGetDefaultValue";
import { useFormSaveAndNavigate } from "@/features/LiveFormEditor/Steps/useFormSaveAndNavigate";
import { DashboardTranslationKey } from "@/public/@types/translations";

export const DonationLiveEditorStepSuggestedDonationAmounts: FC = () => {
  const { apiFormObject } = useLiveFormEditorContext<"donation-form">();
  const { t } = useTranslate();

  const defaultValues = useGetDefaultValue({ apiFormObject });
  const form = useForm<StepSuggestedDonationAmountsFormData>({
    defaultValues,
  });
  const { isDirty } = form.formState;

  const { mutateAsync: updateSuggestedAmounts } = trpc.updateSuggestedAmounts.useMutation();

  const { handleSaveAndNext, handleSaveAndBack } = useFormSaveAndNavigate<StepSuggestedDonationAmountsFormData>({
    type: "donation-form",
    form,
    alwaysSaveOnNext: !apiFormObject.isAmountsAlreadySavedInCreation, // We want to save the amounts the first time the user goes to the step so that it saves the amounts in the backend (even if the form is not dirty)
    saveData: async (formData) => {
      const amounts = getAmountsToSave({ formData });
      const preselectThirdAmount = formData.preselectThirdAmount ?? true;
      const defaultAmountsFrequency = formData.defaultAmountsFrequency as ProductDonationRecurrenceInterval;

      return await updateSuggestedAmounts({
        donationFormId: apiFormObject.id,
        toSave: amounts,
        preselectThirdAmount,
        defaultAmountsFrequency,
        promoteMonthlyGiving: formData.promoteMonthlyGiving,
        smartAmountsEnabled: formData.smartAmountsEnabled,
      });
    },
  });

  useUpdateDonationDataInPreview({
    form,
    mutate: (formData, liveDataToMutate) => {
      const donationFormAmountsInPreview = getAmountsToSave({ formData });
      const existingAmounts = liveDataToMutate.donation.donationFormFields![0].donationFormAmounts ?? [];
      liveDataToMutate.donation.donationFormFields![0].donationFormAmounts = donationFormAmountsInPreview.map(
        (amount, index) => ({ ...amount, id: existingAmounts[index]?.id ?? v4() })
      );

      liveDataToMutate.donation.defaultAmountsFrequency =
        formData.defaultAmountsFrequency as ProductDonationRecurrenceInterval;

      // If all the switches are disabled -> placeholder with ghost preview
      liveDataToMutate.placeholders.isAmounts = getEnabledFrequencies(formData).length === 0;

      liveDataToMutate.donation.promoteMonthlyGiving = formData.promoteMonthlyGiving;

      liveDataToMutate.donation.smartAmountsEnabled = formData.smartAmountsEnabled;

      // If the form is dirty, it means the amounts have been changed, so we need to set the flag to true so that the step "anchor" disappears
      if (isDirty) {
        liveDataToMutate.donation.isAmountsAlreadySavedInCreation = true;
      }
    },
  });

  const handleSwitchChange = (name: keyof StepSuggestedDonationAmountsFormData, checked: boolean) => {
    form.setValue(name, checked, { shouldDirty: true });

    if (!checked && name === "monthlyAmountSwitch") {
      form.setValue("promoteMonthlyGiving", false, { shouldDirty: true });
    }

    const frequency = DONATION_FREQUENCIES.find((donationFrequency) => donationFrequency.switchName === name);
    if (checked && frequency) {
      const savedAmounts = defaultValues[frequency.amountsName];
      form.setValue(
        frequency.amountsName,
        resizeAmounts(
          savedAmounts.length ? savedAmounts : generateDefaultAmounts(frequency.interval),
          frequency.interval,
          form.getValues("suggestedAmountsCount")
        ),
        { shouldDirty: true }
      );
      if (name === "monthlyAmountSwitch") {
        form.setValue("promoteMonthlyGiving", true, { shouldDirty: true });
      }
    }
  };

  const placeholders: DashboardTranslationKey[] = [
    "liveFormEditor.stepSuggestedDonationAmounts.firstItemPlaceholder",
    "liveFormEditor.stepSuggestedDonationAmounts.impactPlaceholderSecond",
    "liveFormEditor.stepSuggestedDonationAmounts.impactPlaceholderThird",
    "liveFormEditor.stepSuggestedDonationAmounts.impactPlaceholderFourth",
  ];

  const handlePreselectThirdAmountChange = (checked: boolean) => {
    handleSwitchChange("preselectThirdAmount", checked);
  };

  const handleSuggestedAmountsCountChange = (suggestedAmountsCount: SuggestedAmountsCount) => {
    form.setValue("suggestedAmountsCount", suggestedAmountsCount, { shouldDirty: true });
    DONATION_FREQUENCIES.forEach((frequency) => {
      form.setValue(
        frequency.amountsName,
        resizeAmounts(form.getValues(frequency.amountsName), frequency.interval, suggestedAmountsCount),
        { shouldDirty: true }
      );
    });
  };

  const hasSuggestedAmounts = form.watch("suggestedAmountsCount") > 0;

  return (
    <FormStepContainer
      title={t("dashboard", "liveFormEditor.stepSuggestedDonationAmounts.title")}
      description={t("dashboard", "liveFormEditor.stepSuggestedDonationAmounts.subtitle")}
      form={form}
      actionsProps={{
        onNext: handleSaveAndNext,
        onBack: handleSaveAndBack,
      }}
    >
      <Stack direction="column" spacing={2} marginTop={1}>
        <LiveEditorBlock>
          <GivingFrequenciesAndAmount form={form} placeholders={placeholders} handleSwitchChange={handleSwitchChange} />
        </LiveEditorBlock>

        <Collapse in={hasSuggestedAmounts} timeout={300} easing="ease-in-out" unmountOnExit>
          <LiveEditorBlock>
            <SmartAmountsBlock form={form} />
          </LiveEditorBlock>
        </Collapse>

        <Collapse
          in={hasSuggestedAmounts && !form.watch("smartAmountsEnabled")}
          timeout={300}
          easing="ease-in-out"
          unmountOnExit
        >
          <LiveEditorBlock>
            <SpecifyImpactForAmountSelected form={form} placeholders={placeholders} />
          </LiveEditorBlock>
        </Collapse>

        <LiveEditorBlock>
          <DefaultOptionsBlock
            form={form}
            onPreselectThirdAmountChange={handlePreselectThirdAmountChange}
            onSuggestedAmountsCountChange={handleSuggestedAmountsCountChange}
          />
        </LiveEditorBlock>

        <Collapse in={form.watch("monthlyAmountSwitch")} timeout={300} easing="ease-in-out" unmountOnExit>
          <LiveEditorBlock>
            <PromoteMonthlyGivingBlock form={form} />
          </LiveEditorBlock>
        </Collapse>
      </Stack>
    </FormStepContainer>
  );
};
